/**
 * Chat System Mutation Hooks
 * React Query hooks for chat mutations (send message, delete session, etc.)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  sendMessage,
  deleteSession,
  clearSessions,
  archiveSession,
} from '../services/chatApi';
import { chatKeys } from './useChat';
import type {
  SendMessageRequest,
  ClearSessionsRequest,
} from '../types/help.types';

/**
 * Hook for chat mutations (send message, manage sessions)
 */
export const useChatMutations = () => {
  const queryClient = useQueryClient();

  /**
   * Invalidate all chat-related queries
   */
  const invalidateChat = () => {
    queryClient.invalidateQueries({ queryKey: chatKeys.all });
  };

  /**
   * Send a message to the AI chatbot
   */
  const sendMessageMutation = useMutation({
    mutationFn: (request: SendMessageRequest) => sendMessage(request),
    onSuccess: (data) => {
      // Invalidate the specific session to get updated messages
      if (data.sessionId) {
        queryClient.invalidateQueries({
          queryKey: chatKeys.sessionDetail(data.sessionId)
        });
      }
      // Invalidate session list to update last message time
      queryClient.invalidateQueries({
        queryKey: chatKeys.sessions()
      });
      // Invalidate stats to update message count and limits
      queryClient.invalidateQueries({
        queryKey: chatKeys.stats()
      });
    },
  });

  /**
   * Delete a chat session
   */
  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) => deleteSession(sessionId),
    onSuccess: () => {
      invalidateChat();
    },
  });

  /**
   * Clear all chat sessions (optionally keep active ones)
   */
  const clearSessionsMutation = useMutation({
    mutationFn: (request?: ClearSessionsRequest) => clearSessions(request),
    onSuccess: () => {
      invalidateChat();
    },
  });

  /**
   * Archive a chat session (mark as inactive)
   */
  const archiveSessionMutation = useMutation({
    mutationFn: (sessionId: string) => archiveSession(sessionId),
    onSuccess: (_, sessionId) => {
      // Invalidate the specific session
      queryClient.invalidateQueries({
        queryKey: chatKeys.sessionDetail(sessionId)
      });
      // Invalidate session list to update active status
      queryClient.invalidateQueries({
        queryKey: chatKeys.sessions()
      });
      // Invalidate stats to update session counts
      queryClient.invalidateQueries({
        queryKey: chatKeys.stats()
      });
    },
  });

  return {
    sendMessage: sendMessageMutation,
    deleteSession: deleteSessionMutation,
    clearSessions: clearSessionsMutation,
    archiveSession: archiveSessionMutation,
    isLoading:
      sendMessageMutation.isPending ||
      deleteSessionMutation.isPending ||
      clearSessionsMutation.isPending ||
      archiveSessionMutation.isPending,
  };
};

export default useChatMutations;
